/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useDialRuleRestrictionData.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Hook for fetching Dial Rule Restriction data.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { fetchDialRuleRestrictionById } from '../api/dialRuleRestrictionsApi';
import { FormState, RuleFormState } from '../types';
import { getDefaultFormState, createClientId } from '../utils/formDefaults';
import { applySequentialPriorities } from '../utils/ruleHelpers';

const ID_PATTERN = /^(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i; // Accepts numeric and Sqid-encoded IDs


/**
 * Hook for fetching Dial Rule Restriction data
 */
export function useDialRuleRestrictionData(id: string | undefined, duplicateId: string | null) {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<FormState | null>(null);
  const { showError } = useNotifications();
  const { t } = useI18n();
  const showErrorRef = useRef(showError);
  const lastLoadedIdRef = useRef<string | null>(null);
  const lastLocationKeyRef = useRef<string>('');
  const tRef = useRef(t);

  useEffect(() => {
    showErrorRef.current = showError;
    tRef.current = t;
  }, [showError, t]);

  const loadDialRuleRestriction = useCallback(
    async (loadId: string, duplicate: boolean = false) => {
      setIsLoading(true);
      try {
        const payload = await fetchDialRuleRestrictionById(loadId, tRef.current);

        const suffix = tRef.current('dialRuleRestrictions.actions.duplicate');
        const rules = applySequentialPriorities(
          (payload.rules ?? []).map((rule) => ({
            ...rule,
            clientId: rule.id ? `rule-${rule.id}` : createClientId(),
          }))
        );

        const normalised: FormState = {
          name: duplicate
            ? `${payload.name ?? ''} (${suffix})`.trim()
            : (payload.name ?? '').trim(),
          description: (payload.description ?? '').trim(),
          enabled: payload.enabled ?? true,
          rules,
        };

        setFormData(normalised);
      } catch (error) {
        showErrorRef.current(tRef.current('dialRuleRestrictions.messages.loadError'));
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    const loadId = duplicateId || id;
    const savedDataKey = 'dialRuleRestrictions:saveAndNewData';
    const locationKey = location.key || location.pathname + location.search;

    if (!loadId || loadId === 'new') {
      // Check if we need to reset:
      // 1. ID changed
      // 2. Location key changed (React Router navigation)
      // 3. sessionStorage timestamp indicates reset needed
      const resetTimestamp = sessionStorage.getItem(savedDataKey + ':reset');
      const lastResetTimestamp = lastLoadedIdRef.current === 'new' ?
        sessionStorage.getItem(savedDataKey + ':lastReset') : null;

      const shouldReset = lastLoadedIdRef.current !== 'new' ||
        lastLocationKeyRef.current !== locationKey ||
        (lastLoadedIdRef.current === 'new' && loadId === 'new' &&
          resetTimestamp && resetTimestamp !== lastResetTimestamp);

      if (shouldReset) {
        lastLoadedIdRef.current = 'new';
        lastLocationKeyRef.current = locationKey;

        // Clear sessionStorage for "Save and New" functionality
        try {
          sessionStorage.removeItem(savedDataKey);
          if (resetTimestamp) {
            sessionStorage.setItem(savedDataKey + ':lastReset', resetTimestamp);
            sessionStorage.removeItem(savedDataKey + ':reset');
          }
        } catch (error) {
          // Ignore errors when clearing
        }

        if (!duplicateId) {
          // Reset form to default values
          setFormData(getDefaultFormState());
        }
      }
      return;
    }

    if (!ID_PATTERN.test(loadId)) {
      showErrorRef.current(tRef.current('dialRuleRestrictions.messages.loadError'));
      lastLoadedIdRef.current = null;
      return;
    }

    if (lastLoadedIdRef.current === loadId && lastLocationKeyRef.current === locationKey) {
      return;
    }

    lastLoadedIdRef.current = loadId;
    lastLocationKeyRef.current = locationKey;
    loadDialRuleRestriction(loadId, Boolean(duplicateId)).catch(() => {
      lastLoadedIdRef.current = null;
    });
  }, [duplicateId, id, loadDialRuleRestriction, location.key, location.pathname, location.search]);

  return {
    isLoading,
    formData,
    setFormData,
  };
}

